import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { waPlain } from "@/lib/whatsapp";

const NAV = [
  { label: "Início", to: "/", id: "inicio" },
  { label: "Bonés", to: "/catalogo", id: "bones" },
  { label: "Bonés Importados", to: "/importados", id: "importados" },
  { label: "Marcas", to: "/#marcas", id: "marcas" },
  { label: "Como Comprar", to: "/#como-comprar", id: "como-comprar" },
  { label: "Instagram", to: "/#instagram", id: "instagram" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "glass-dark border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="header-logo" className="font-display text-lg font-extrabold tracking-[0.18em] text-offwhite">
            DM<span className="mx-2 inline-block h-1.5 w-1.5 -translate-y-0.5 rounded-full bg-silver" aria-hidden="true" />IMPORTS
          </Link>

          <nav data-testid="header-nav" className="hidden items-center gap-6 lg:flex">
            {NAV.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                data-testid={`nav-link-${item.id}`}
                className="group relative text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-offwhite"
              >
                {item.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-silver transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waPlain()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-header-cta"
              className="hidden items-center gap-2 rounded-full border border-silver/30 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-offwhite transition-colors duration-300 hover:border-silver hover:bg-offwhite hover:text-obsidian sm:flex"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Abrir menu"
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-offwhite lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-obsidian/95 backdrop-blur-2xl"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <span className="font-display text-lg font-extrabold tracking-[0.18em]">DM IMPORTS</span>
              <button
                data-testid="mobile-menu-close"
                aria-label="Fechar menu"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ y: 28, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.id}`}
                    onClick={() => setOpen(false)}
                    className="block border-b border-white/10 py-4 font-display text-3xl font-bold uppercase tracking-tight text-offwhite"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                initial={{ y: 28, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                href={waPlain()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-cta"
                className="mt-8 flex items-center justify-center gap-3 rounded-full bg-offwhite py-4 text-sm font-bold uppercase tracking-[0.2em] text-obsidian"
              >
                <MessageCircle className="h-5 w-5" />
                Falar no WhatsApp
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
